/**
 * Contact role taxonomy.
 *
 * Two parallel role concepts on a Contact row:
 *
 *  - operationalRoles (OURS):  what the contact does for the TMC program
 *    — a contact can have multiple at once. Set by CSM during onboarding.
 *
 *  - salesRole (HUBSPOT):  what the contact played in the sales deal
 *    — single value. Mirrored read-only from HubSpot.
 *
 * Keeping these as runtime constants (vs Prisma enums) so we can extend
 * without a migration; the Postgres column type is text[] / text.
 */

export type OperationalRole =
  | 'TRAVEL_MANAGER'
  | 'TRAVEL_COORDINATOR'
  | 'AP_FINANCE'
  | 'IT_SSO'
  | 'EXECUTIVE_SPONSOR'
  | 'DUTY_OF_CARE'
  | 'HR_OWNER'
  | 'PROCUREMENT'
  | 'EXECUTIVE_ASSISTANT'
  | 'VIP_TRAVELER_LIAISON'
  | 'AFTER_HOURS_ESCALATION'
  | 'OTHER';

export const OPERATIONAL_ROLES: OperationalRole[] = [
  'TRAVEL_MANAGER',
  'TRAVEL_COORDINATOR',
  'AP_FINANCE',
  'IT_SSO',
  'EXECUTIVE_SPONSOR',
  'DUTY_OF_CARE',
  'HR_OWNER',
  'PROCUREMENT',
  'EXECUTIVE_ASSISTANT',
  'VIP_TRAVELER_LIAISON',
  'AFTER_HOURS_ESCALATION',
  'OTHER',
];

export const OPERATIONAL_ROLE_LABEL: Record<OperationalRole, string> = {
  TRAVEL_MANAGER: 'Travel Manager',
  TRAVEL_COORDINATOR: 'Travel Coordinator',
  AP_FINANCE: 'AP / Finance',
  IT_SSO: 'IT / SSO',
  EXECUTIVE_SPONSOR: 'Executive Sponsor',
  DUTY_OF_CARE: 'Duty of Care',
  HR_OWNER: 'HR Owner',
  PROCUREMENT: 'Procurement',
  EXECUTIVE_ASSISTANT: 'Executive Assistant',
  VIP_TRAVELER_LIAISON: 'VIP Traveler Liaison',
  AFTER_HOURS_ESCALATION: 'After-hours Escalation',
  OTHER: 'Other',
};

export const OPERATIONAL_ROLE_DESCRIPTION: Record<OperationalRole, string> = {
  TRAVEL_MANAGER: 'Owns the corporate travel program; primary CSM contact.',
  TRAVEL_COORDINATOR: 'Operational, day-to-day travel desk contact.',
  AP_FINANCE: 'Owns invoicing, reconciliation, central-card disputes.',
  IT_SSO: 'Owns auth / SSO / OBT integration; admin of the OBT site.',
  EXECUTIVE_SPONSOR: 'C-level sponsor; QBR attendee + escalation point.',
  DUTY_OF_CARE: 'Owns risk events, traveler tracking, after-hours escalation.',
  HR_OWNER: 'Owns the HR / traveler-list feed; new-hire ramping.',
  PROCUREMENT: 'Owns contracts, MSA, renewal negotiation.',
  EXECUTIVE_ASSISTANT: 'Books on behalf of one or more executives.',
  VIP_TRAVELER_LIAISON: 'Handles board / C-suite travel + sensitive trips.',
  AFTER_HOURS_ESCALATION: 'Reachable for emergencies outside business hours.',
  OTHER: 'Other / unclassified.',
};

export function isOperationalRole(v: string): v is OperationalRole {
  return (OPERATIONAL_ROLES as string[]).includes(v);
}

// HubSpot-side sales role taxonomy. Mirrored read-only.
export type SalesRole =
  | 'champion'
  | 'decision_maker'
  | 'influencer'
  | 'blocker';
export const SALES_ROLES: SalesRole[] = [
  'champion',
  'decision_maker',
  'influencer',
  'blocker',
];
export const SALES_ROLE_LABEL: Record<SalesRole, string> = {
  champion: 'Champion',
  decision_maker: 'Decision Maker',
  influencer: 'Influencer',
  blocker: 'Blocker',
};

export type ContactStatus =
  | 'active'
  | 'left_company'
  | 'unsubscribed'
  | 'bounced'
  | 'merged';
export const CONTACT_STATUSES: ContactStatus[] = [
  'active',
  'left_company',
  'unsubscribed',
  'bounced',
  'merged',
];
export const CONTACT_STATUS_LABEL: Record<ContactStatus, string> = {
  active: 'Active',
  left_company: 'Left company',
  unsubscribed: 'Unsubscribed',
  bounced: 'Bounced',
  merged: 'Merged (HubSpot)',
};
